import React, { useContext, useEffect, useState } from "react"
import { Accordion, Divider, Form, Icon, Message } from "semantic-ui-react"
import { discountType } from "../../constants"
import { CampProps, CampingStyleProps } from "../../interfaces"
import CampDiscountProps from "../../interfaces/CampDiscountProps"
import { displayPercentage } from "../../utils"
import Button from "../ui/Button"

import DiscountOption from "./DiscountOption"
import { BookerContext } from "../../contexts/BookerContext"
import { formatMoney, formatMoneyFromCents } from "../../utils/formatMoney"
import api from "../../api2"
import InputFieldWithLabel from "../Admin/Inputs/InputFieldWithLabel"
import Alert from "../ui/Alert"
import classNames from "../../utils/classNames"
import ICoupon from "../../interfaces/ICoupon"

interface ComponentProps {
  camp: CampProps
  couponsCount: number
}

const CouponForm = ({ camp, couponsCount }: ComponentProps) => {
  const { secondaryColor, couponCode, setCouponCode, startDate, endDate } = useContext(BookerContext)

  const [isCouponFormActive, setIsCouponFormActive] = useState<boolean>(!!couponCode)
  const [codeStr, setCodeStr] = useState("")
  const [couponError, setCouponError] = useState("")
  const [couponSuccess, setCouponSuccess] = useState("")
  const [isLoadingCouponCode, setIsLoadingCouponCode] = useState(false)

  const validateCoupon = () => {
    setCouponCode(null)
    setCouponError(null)
    setCouponSuccess(null)
    setIsLoadingCouponCode(true)

    api.Coupons.validate(codeStr, camp.id, startDate.toISOString(), endDate.toISOString())
      .then(({ coupon }) => {
        setCouponCode(coupon.code)
        setCouponSuccess(appliedMessageForCoupon(coupon))
        setIsLoadingCouponCode(false)
      })
      .catch((err) => {
        setCouponError(err.message)
        setIsLoadingCouponCode(false)
      })
  }

  const appliedMessageForCoupon = (coupon: ICoupon) => {
    if (!coupon) return ""

    if (coupon.percent_off) {
      return `Discount of ${displayPercentage(parseFloat(coupon.percent_off))} has been applied to your total.`
    } else if (coupon.amount_off_in_cents) {
      return `Discount of ${formatMoneyFromCents(coupon.amount_off_in_cents, camp.currency)} per night has been removed from your total.`
    } else if (coupon.flat_rate_in_cents) {
      return `Discounted rate of ${formatMoneyFromCents(coupon.flat_rate_in_cents, camp.currency)} has been applied to your subtotal.`
    } else if (coupon.subtotal_amount_off_in_cents) {
      return `Discount of ${formatMoneyFromCents(coupon.subtotal_amount_off_in_cents, camp.currency)} has been applied to your total.`
    }
  }

  return (
    <>
      {couponsCount ? (
        <Accordion styled fluid className="relative z-20">
          <Accordion.Title
            active={isCouponFormActive}
            index={0}
            onClick={() => setIsCouponFormActive(!isCouponFormActive)}
          >
            <Icon name="dropdown" />
            Add Coupon
          </Accordion.Title>

          <Accordion.Content active={isCouponFormActive}>
            <Form loading={isLoadingCouponCode} success={!!couponCode} className="flex flex-col xs:block">
              <InputFieldWithLabel
                label="Code"
                value={codeStr}
                onChange={(e) => setCodeStr(e.target.value.toUpperCase())}
                className="max-w-md"
              />
              {couponError && (
                <div className="mt-4">
                  <Alert description={couponError} className="max-w-md" />
                </div>
              )}
              {couponSuccess?.length ? (
                <div className="mt-4">
                  <Alert kind="success" description={couponSuccess} className="max-w-md" />
                </div>
              ) : (
                <></>
              )}
              <div className="mt-4">
                <Button
                  style={{ backgroundColor: secondaryColor }}
                  className="hover:opacity-80 text-white"
                  disabled={isLoadingCouponCode || !codeStr}
                  onClick={validateCoupon}
                >
                  Apply Coupon
                </Button>
                <Button
                  variant="none"
                  style={{ color: secondaryColor }}
                  className={classNames(
                    "ml-1 hover:opacity-80 underline",
                    (isLoadingCouponCode || !codeStr) && "pointer-events-none",
                  )}
                  disabled={isLoadingCouponCode || !codeStr}
                  onClick={() => {
                    setCouponCode("")
                    setCodeStr("")
                    setCouponSuccess("")
                  }}
                >
                  Remove Coupon
                </Button>
              </div>
            </Form>
          </Accordion.Content>
        </Accordion>
      ) : (
        <></>
      )}
    </>
  )
}

export default CouponForm
